/*
  * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
  * SPDX-License-Identifier: MIT
  *
  * Licensed under the MIT License. See the LICENSE accompanying this file
  * for the specific language governing permissions and limitations under
  * the License.
  */

import React from 'react';
import { connect } from 'react-redux';

import { Auth } from 'aws-amplify';
import { I18n } from '@aws-amplify/core';

import Config from "Config";

import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import { MailOutline, Smartphone } from '@material-ui/icons';

import { Branding } from '../../branding';
import { getCurrentBrand } from '../../utils/applyBrand';
import LogOutButton from '../../components/LogoutButton/LogoutButton';
import VerifyAttributeDialog from '../../components/VerifyAttributeDialog/VerifyAttributeDialog';
import ChangePasswordDialog from '../../components/ChangePasswordDialog/ChangePasswordDialog';
import AppSnackbar from '../../components/Snackbar/Snackbar';
import FormInlineError from '../../components/FormInlineError/FormInlineError';
import { setUser, setUserEmail, setUserPhonenumber } from '../../redux/actions';
import { isJapanesePhoneNumber, formatPhoneNumberForJapan, displayPhoneNumber } from '../../utils/phoneNumber';
import { API } from 'aws-amplify';

const EVENTS_PRIMARY = '#9ec317';

const useStyles = makeStyles((theme) => ({
	formWrap: {
		textAlign: 'left',
	},
	boxInputField: {
		margin: theme.spacing(2),
	},
	boxInputFieldEmailMode: {
		margin: theme.spacing(0.5, 0, 0),
	},
	emailChangePageTitle: {
		marginBottom: theme.spacing(1.5),
		fontWeight: 600,
		color: theme.palette.text.primary,
		textAlign: 'left',
		width: '100%',
	},
	emailChangeDescription: {
		marginBottom: theme.spacing(2),
		textAlign: 'left',
		width: '100%',
	},
	emailChangeForm: {
		width: '100%',
	},
	emailLoginFooterActions: {
		marginTop: theme.spacing(3),
	},
	buttonChangePrimary: {
		width: '100%',
	},
	buttonCancelNeutral: {
		width: '100%',
		color: theme.palette.grey[700],
		borderColor: theme.palette.grey[400],
		'&:hover': {
			borderColor: theme.palette.grey[500],
			backgroundColor: theme.palette.grey[50],
		},
	},
	settingsFooterActions: {
		display: 'flex',
		flexDirection: 'column',
		gap: '12px',
		width: '100%',
		marginTop: theme.spacing(2),
	},
	textFieldIcon: {
		color: Branding.secondary,
	},
	formControl: {
		width: "-webkit-fill-available"
	},
	emailOutlinedField: {
		marginTop: theme.spacing(0.5),
		width: '100%',
		'& .MuiOutlinedInput-root': {
			width: '100%',
		},
	},
	emailOutlinedFieldEvents: {
		'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: EVENTS_PRIMARY,
		},
		'& .MuiOutlinedInput-root.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: EVENTS_PRIMARY,
		},
		'& .MuiInputLabel-outlined.Mui-focused': {
			color: EVENTS_PRIMARY,
		},
	},
	input: {

	},
	buttonCancel: {
		marginTop: theme.spacing(1),
		marginLeft: theme.spacing(1),
		color: Branding.negative,
		'&:hover': {
			color: Branding.negative,
			opacity: Branding.opacityHover,
		},
	},
	buttonSave: {
		marginTop: theme.spacing(1),
		marginLeft: theme.spacing(1),
		color: Branding.positive,
		'&:hover': {
			color: Branding.positive,
			opacity: Branding.opacityHover,
		},
	},
	buttonPasswordChange: {
		width: '100%',
	}
}));

const mapStateToProps = (state) => {
	const attributes = state.user?.attributes || {};
	const rawPhoneNumber = attributes.phone_number || '';
	return {
		lang: state.app.lang,

		username: state.user?.username || '',
		email: attributes.email || '',
		email_verified: attributes.email_verified || false,
		phone_number: rawPhoneNumber,
		// 表示用の電話番号（E.164形式から国内形式に変換）
		phone_number_display: displayPhoneNumber(rawPhoneNumber),
		phone_number_verified: attributes.phone_number_verified || false,
	}
}

const TabSignInData = (props) => {
	const classes = useStyles();
	const brand = getCurrentBrand();
	const mode = props.mode || 'all';
	const [editEmail, setEditEmail] = React.useState(mode === 'email');
	const [editingEmailValue, setEditingEmailValue] = React.useState('');
	const [editPhoneNumber, setEditPhoneNumber] = React.useState(mode === 'phone');
	const [editingPhoneValue, setEditingPhoneValue] = React.useState('');
	const [passwordDialogOpen, setPasswordDialogOpen] = React.useState(false);
	const [verifyAttribute, setVerifyAttribute] = React.useState({
		type: '',
		open: false
	});
	const [snackBarOps, setSnackBarOps] = React.useState({
		type: 'info',
		open: false,
		vertical: 'top',
		horizontal: 'center',
		autoHide: 0,
		message: ''
	});
	const [formError, setFormError] = React.useState('');

	const emailInputValue = mode === 'email' ? editingEmailValue : props.email;

	const resolveRedirectUri = () =>
		props.redirectUri || localStorage.getItem('client-redirect-uri') || '';
	const resolveClientState = () =>
		props.clientState || localStorage.getItem('client-state') || '';

	const hasExternalRedirect = () => !!resolveRedirectUri();

	const redirectToClient = () => {
		const base = resolveRedirectUri();
		if (!base) return;
		let url = base;
		const st = resolveClientState();
		if (st) {
			url += (url.includes('?') ? '&' : '?') + 'state=' + encodeURIComponent(st);
		}
		window.location.href = url;
	};

	// To initiate the process of verifying the attribute like 'phone_number' or 'email'
	const verifyCurrentUserAttribute = (attr) => {
		Auth.verifyCurrentUserAttribute(attr)
			.then(() => {
				setVerifyAttribute({ type: attr, open: true });
			}).catch((err) => {
				console.log(err);
				props.reloadUserData();
				setFormError(I18n.get('TAB_SIGNIN_DATA_MESSAGE_EROR'));
			});
	}

	const callHerokuSync = async (accountKey, attr, jsonAttributes) => {
		const syncBody = { accountKey };
		if (attr === 'email') syncBody.email = jsonAttributes.email;
		if (attr === 'phone_number') syncBody.tel = jsonAttributes.phone_number;
		await API.post('amplifyIdentityBrokerApi', '/herokuSync', { body: syncBody });
	};

	const showError = (messageKey) => {
		setFormError(I18n.get(messageKey));
	};

	const showSnackBar = (ops) => {
		setFormError('');
		setSnackBarOps(ops);
	};

	const updateUserAttributes = async (attributes, attr = null) => {
		const jsonAttributes = JSON.parse(attributes);
		const isHerokuTarget = (attr === 'email' || attr === 'phone_number');
		const isHerokuEnabled = Config.isHerokuEnabled;

		try {
			const user = await Auth.currentAuthenticatedUser();

			if (isHerokuTarget && isHerokuEnabled) {
				const accountKey = user.username;

				const oldValues = {};
				if (attr === 'email') oldValues.email = user.attributes.email;
				if (attr === 'phone_number') oldValues.phone_number = user.attributes.phone_number;

				try {
					await callHerokuSync(accountKey, attr, jsonAttributes);
				} catch (herokuErr) {
					console.error('HerokuSync failed:', herokuErr);
					showError('TAB_SIGNIN_DATA_MESSAGE_HEROKU_SYNC_FAILED');
					return;
				}

				try {
					await Auth.updateUserAttributes(user, jsonAttributes);
				} catch (cognitoErr) {
					console.error('Cognito update failed, rolling back Heroku:', cognitoErr);
					try {
						await callHerokuSync(accountKey, attr, oldValues);
					} catch (rollbackErr) {
						console.error('Heroku rollback failed:', rollbackErr);
					}
					showError('TAB_SIGNIN_DATA_MESSAGE_EROR');
					return;
				}

				setVerifyAttribute({ type: attr, open: true });
				showSnackBar({
					type: 'info', open: true, vertical: 'top', horizontal: 'center',
					autoHide: 3000, message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_UPDATE_ATTRIBUTE_VERIFYCATION_REQUEST')
				});
			} else if (isHerokuTarget) {
				await Auth.updateUserAttributes(user, jsonAttributes);
				setVerifyAttribute({ type: attr, open: true });
				showSnackBar({
					type: 'info', open: true, vertical: 'top', horizontal: 'center',
					autoHide: 3000, message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_UPDATE_ATTRIBUTE_VERIFYCATION_REQUEST')
				});
			} else {
				await Auth.updateUserAttributes(user, jsonAttributes);
				showSnackBar({
					type: 'success', open: true, vertical: 'top', horizontal: 'center',
					autoHide: 3000, message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_UPDATE_ATTRIBUTE_SUCCESS')
				});
			}

			props.reloadUserData();
		} catch (err) {
			console.error('updateUserAttributes error:', err);
			showError('TAB_SIGNIN_DATA_MESSAGE_EROR');
		}
	};

	const handleEmailChange = (value) => {
		setEditEmail(true);
		if (mode === 'email') {
			setEditingEmailValue(value);
		} else {
			props.setUserEmail(value);
		}
	};

	const handlePhoneNumberChange = (value) => {
		//if (!value) return;

		setEditPhoneNumber(true);
		setEditingPhoneValue(value);
	};

	const handleAttributeChange = (attr) => {
		switch (attr) {
			case 'email':
				setEditEmail(true);
				break;
			case 'phone_number':
				setEditPhoneNumber(true);
				break;
			case 'password':
				setPasswordDialogOpen(true);
				break;
			default:
				break;
		}
	};

	const handleAttributeCancel = (attr) => {
		switch (attr) {
			case 'email':
				setEditEmail(false);
				if (hasExternalRedirect()) {
					redirectToClient();
					return;
				}
				if (mode === 'email') {
					setEditingEmailValue('');
				}
				break;
			case 'phone_number':
				setEditPhoneNumber(false);
				setEditingPhoneValue('');
				if (hasExternalRedirect()) {
					redirectToClient();
					return;
				}
				break;
			default:
				break;
		}

		props.reloadUserData();
	};

	const handleAttributeSave = (attr) => {
		switch (attr) {
			case 'email':
				if (emailInputValue?.trim() && validateEmailAddress(emailInputValue)) {
					updateUserAttributes(`{"email": "${emailInputValue}"}`, 'email');
					setEditEmail(false);
					if (mode === 'email') {
						setEditingEmailValue('');
					}
				} else {
					setFormError(I18n.get('TAB_SIGNIN_DATA_MESSAGE_EMAIL_VALIDATION_ERROR'));
				}
				break;
			case 'phone_number':
				const formattedPhone = formatPhoneNumberForSave(editingPhoneValue);
				if (validatePhoneNumber(formattedPhone)) {
					updateUserAttributes(`{"phone_number": "${formattedPhone}"}`, 'phone_number');
					setEditPhoneNumber(false);
					setEditingPhoneValue('');
				} else {
					setFormError(I18n.get('TAB_SIGNIN_DATA_MESSAGE_PHONE_NUMBER_VALIDATION_ERROR'));
				}
				break;
			default:
				break;
		}
	};

	const handleCloseVerifyDialog = async (successful = false, attrType = '') => {
		if ((attrType === 'email' || attrType === 'phone_number') && successful === true) {
			if (hasExternalRedirect()) {
				redirectToClient();
				return;
			}
			try {
				await Auth.signOut();
				window.location.href = '/';
				return;
			} catch (err) {
				console.log('Error signing out:', err);
			}
		}

		if (successful === true) {
			showSnackBar({
				type: 'success',
				open: true,
				vertical: 'top',
				horizontal: 'center',
				autoHide: 3000,
				message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_VERIFY_ATTRIBUTE_MESSAGE_SUCCESS')
			});
		}

		setVerifyAttribute({ type: '', open: false })
	}

	const handleClosePasswordDialog = (successful = false) => {
		setPasswordDialogOpen(false);
		if (successful === true) {
			if (hasExternalRedirect()) {
				redirectToClient();
				return;
			}
			showSnackBar({
				type: 'success',
				open: true,
				vertical: 'top',
				horizontal: 'center',
				autoHide: 3000,
				message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_PASSWORD_CHANGE_SUCCESS'),
			});
			return;
		}
		if (hasExternalRedirect()) {
			redirectToClient();
			return;
		}
		props.reloadUserData();
	};

	const validateEmailAddress = (emailAddress = "") => {
		var reg = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/;

		return emailAddress === "" ? true : reg.test(emailAddress) ? true : false;
	}

	const validatePhoneNumber = (phonenumber = "") => {
		var reg = /^\+[0-9]{3,}$/;

		return phonenumber === "" ? true : reg.test(phonenumber) ? true : false;
	}

	// 電話番号の保存用フォーマット（共通ユーティリティを使用）
	const formatPhoneNumberForSave = (phoneNumber) => {
		if (!phoneNumber) return '';
		if (isJapanesePhoneNumber(phoneNumber)) {
			return formatPhoneNumberForJapan(phoneNumber);
		}
		return phoneNumber;
	};

	const showEmail = (mode === 'all' || mode === 'email');
	const showPhone = (mode === 'all' || mode === 'phone');
	const showPassword = (mode === 'all' || mode === 'password');
	const showLogout = (mode === 'all');
	const isEmailOnlyMode = mode === 'email';
	const isPasswordOnlyMode = mode === 'password';

	return (
		<div>
			{snackBarOps.open && snackBarOps.type !== 'error' && (
				<AppSnackbar ops={snackBarOps} />
			)}

			{verifyAttribute.open && (
				<VerifyAttributeDialog
					attrType={verifyAttribute.type}
					open={verifyAttribute.open}
					close={(successful) => handleCloseVerifyDialog(successful, verifyAttribute.type)}
					reloadUserData={props.reloadUserData}
				/>
			)}

			{passwordDialogOpen && !isPasswordOnlyMode && (
				<ChangePasswordDialog
					open={passwordDialogOpen}
					close={(successful) => handleClosePasswordDialog(successful)}
					redirectUri={props.redirectUri}
				/>
			)}

			<div className={classes.formWrap}>
					<FormInlineError message={formError} />
					{showEmail && (
					<Box className={isEmailOnlyMode ? classes.boxInputFieldEmailMode : classes.boxInputField}>
						{isEmailOnlyMode && (
							<div className={classes.emailChangeForm}>
								<Typography variant="h6" component="h1" className={classes.emailChangePageTitle}>
									{I18n.get('TAB_SIGNIN_DATA_EMAIL_CHANGE_PAGE_TITLE')}
								</Typography>
								<Typography variant="body2" color="textSecondary" className={classes.emailChangeDescription}>
									{I18n.get('TAB_SIGNIN_DATA_EMAIL_CHANGE_DESCRIPTION_LINE1')}
								</Typography>
								<Typography variant="body2" color="textSecondary" className={classes.emailChangeDescription}>
									{I18n.get('TAB_SIGNIN_DATA_EMAIL_CHANGE_DESCRIPTION_LINE2')}
								</Typography>
							</div>
						)}
						<TextField
							fullWidth
							variant="outlined"
							id="inputEmail"
							label={
								isEmailOnlyMode
									? I18n.get('TAB_SIGNIN_DATA_EMAIL_CHANGE_INPUT_LABEL')
									: I18n.get('TAB_SIGNIN_DATA_EMAIL_INPUT_LABEL')
							}
							value={emailInputValue}
							onChange={(event) => handleEmailChange(event.target.value)}
							className={[
								isEmailOnlyMode ? classes.emailChangeForm : '',
								classes.emailOutlinedField,
								brand === 'halmekEvents' ? classes.emailOutlinedFieldEvents : '',
							].filter(Boolean).join(' ')}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<MailOutline className={classes.textFieldIcon} />
									</InputAdornment>
								),
							}}
						/>
						{editEmail && (
							isEmailOnlyMode ? (
								<div className={`login-footer-actions ${classes.emailLoginFooterActions}`}>
									<Button
										variant="contained"
										color="primary"
										fullWidth
										onClick={() => handleAttributeSave('email')}
										className={classes.buttonChangePrimary}
										disabled={!emailInputValue?.trim()}
									>
										{I18n.get('TAB_SIGNIN_DATA_EMAIL_CHANGE_SUBMIT_LABEL')}
									</Button>
									<Button
										variant="outlined"
										fullWidth
										onClick={() => handleAttributeCancel('email')}
										className={classes.buttonCancelNeutral}
									>
										{I18n.get('TAB_SIGNIN_DATA_CANCEL_BUTTON_LABEL')}
									</Button>
								</div>
							) : (
								<div>
									<Button
										variant="outlined"
										onClick={() => handleAttributeSave('email')}
										className={classes.buttonSave}
									>
										{I18n.get('TAB_SIGNIN_DATA_SAVE_BUTTON_LABEL')}
									</Button>
									<Button
										variant="outlined"
										onClick={() => handleAttributeCancel('email')}
										className={classes.buttonCancel}
									>
										{I18n.get('TAB_SIGNIN_DATA_CANCEL_BUTTON_LABEL')}
									</Button>
								</div>
							)
						)}
					</Box>
					)}

					{showPhone && (
					<Box className={classes.boxInputField}>
						<FormControl className={classes.formControl}>
							<InputLabel htmlFor="inputPhoneNumber">
								{I18n.get('TAB_SIGNIN_DATA_PHONENUMBER_INPUT_LABEL')}
							</InputLabel>
							<Input
								value={editPhoneNumber ? editingPhoneValue : props.phone_number_display}
								onChange={(event) => handlePhoneNumberChange(event.target.value)}
								id="inputPhoneNumber"
								startAdornment={
									<InputAdornment position="start">
										<Smartphone className={classes.textFieldIcon} />
									</InputAdornment>
								}
								className={classes.input}
								inputProps={{ style: { left: 0 } }}
							/>
						</FormControl>
						{editPhoneNumber && (
							<div>
								<Button
									variant="outlined"
									onClick={() => handleAttributeSave('phone_number')}
									className={classes.buttonSave}
								>
									{I18n.get('TAB_SIGNIN_DATA_SAVE_BUTTON_LABEL')}
								</Button>
								<Button
									variant="outlined"
									onClick={() => handleAttributeCancel('phone_number')}
									className={classes.buttonCancel}
								>
									{I18n.get('TAB_SIGNIN_DATA_CANCEL_BUTTON_LABEL')}
								</Button>
							</div>
						)}
					</Box>
					)}

					{isPasswordOnlyMode && (
					<Box className={classes.boxInputFieldEmailMode}>
						<ChangePasswordDialog
							inline
							close={(successful) => handleClosePasswordDialog(successful)}
							redirectUri={props.redirectUri}
						/>
					</Box>
					)}

					{(showPassword || showLogout) && (
					<Box className={classes.settingsFooterActions}>
						{showPassword && !isPasswordOnlyMode && (
							<Button
								variant="contained"
								color="primary"
								onClick={() => handleAttributeChange('password')}
								className={classes.buttonPasswordChange}
							>
								{I18n.get('TAB_SIGNIN_DATA_CHANGE_PASSWORD_LABEL')}
							</Button>
						)}
						{showLogout && (
							<LogOutButton />
						)}
					</Box>
					)}

			</div>
		</div>
	)
}

export default connect(mapStateToProps, { setUser, setUserEmail, setUserPhonenumber })(TabSignInData)
